import { Link } from '@tanstack/react-router'
import { Button } from '@viastud/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@viastud/ui/dropdown-menu'
import { trpc } from '@viastud/ui/lib/trpc'
import { UserIcon } from '@viastud/ui/shared/user-icon'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@viastud/ui/sheet'
import { Bell, Clock, Crown, HelpCircle, Infinity, Menu, Star, User } from 'lucide-react'

import { useAuthStore } from '@/store/auth.store'

function HeaderNavigationLinks({ grade, mobile = false }: { grade: string; mobile?: boolean }) {
  const className = mobile
    ? 'rounded-lg px-4 py-3 text-lg font-medium transition-colors hover:bg-blue-50'
    : 'flex items-center px-4 py-2'
  const activeClassName = mobile
    ? 'bg-blue-50 text-blue-600'
    : 'text-blue-600 border-b-2 border-b-blue-600'

  return (
    <>
      <Link
        to="/"
        className={className}
        activeProps={{ className: activeClassName }}
        activeOptions={{ exact: true }}
        id={mobile ? undefined : 'btn-student-header-dashboard'}
      >
        Tableau de bord
      </Link>
      <Link
        to="/ressources/$grade/$subject"
        params={{ grade: grade.toLowerCase(), subject: 'maths' }}
        className={className}
        activeProps={{ className: activeClassName }}
        id={mobile ? undefined : 'btn-student-header-resources'}
      >
        Ressources
      </Link>
    </>
  )
}

export default function StudentHeader() {
  const auth = useAuthStore()
  const resourceGrade = auth.user?.grade ?? 'TROISIEME'

  const { data: subscription, isLoading: subscriptionLoading } =
    trpc.user.getStudentSubscriptionDetails.useQuery()
  const { data: tokenBalance } = trpc.user.getTokenBalance.useQuery(undefined, {
    enabled: auth.role === 'STUDENT',
  })

  return (
    <div className="flex w-full justify-center p-4">
      {/* Desktop Header */}
      <div className="h-70px shadow-custom hidden w-5/6 items-center overflow-auto rounded-[68px] bg-white px-8 py-2 md:flex">
        <div className="flex grow items-center justify-between overflow-auto">
          <div className="flex shrink-0 items-center gap-4">
            <Link to="/">
              <img
                src="/logos/viastud-text-logo.png"
                alt="viastud logo"
                className="mb-px h-5 w-auto md:h-9"
              />
            </Link>
            <HeaderNavigationLinks grade={resourceGrade} />
          </div>
          <div className="flex items-center gap-3">
            {subscriptionLoading ? (
              <div className="h-9 w-[250px] animate-pulse rounded-full bg-gray-100" />
            ) : subscription?.status === 'ACTIVE' || subscription?.status === 'SUSPENDED' ? (
              <div className="flex items-center gap-3">
                <div className="inline-flex items-center gap-2 rounded-full border border-yellow-300 bg-gradient-to-r from-yellow-300 via-amber-300 to-yellow-400 px-3 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-yellow-200/60">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/30 shadow-inner">
                    {String(subscription?.planId) === '1' ? (
                      <Star className="h-3.5 w-3.5 text-yellow-900" />
                    ) : (
                      <Crown className="h-3.5 w-3.5 text-yellow-900" />
                    )}
                  </span>
                  <span className="tracking-wide">Premium</span>
                </div>
                <div className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2">
                  <span className="text-sm font-medium text-gray-700">Cours en Illimité</span>
                  <Infinity className="size-4 text-gray-600" />
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/settings">
                  <Button variant="default">S&#39;abonner</Button>
                </Link>
                {auth.role === 'STUDENT' && tokenBalance && (
                  <div className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2">
                    <Clock className="size-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">
                      Cours disponibles : {tokenBalance.balance}
                    </span>
                  </div>
                )}
              </div>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="none" asChild>
                  <div className="cursor-pointer">
                    <UserIcon firstName={auth.user?.firstName} lastName={auth.user?.lastName} />
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem className="flex flex-col items-stretch focus:bg-blue-50">
                  <Link className="flex items-center gap-2" to="/profile">
                    <User className="size-4" />
                    <p className="text-sm font-medium text-gray-950">Profil</p>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="flex flex-col items-stretch focus:bg-blue-50">
                  <Link className="flex items-center gap-2" to="/settings">
                    <Bell className="size-4" />
                    <p className="text-sm font-medium text-gray-950">Abonnement</p>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="flex flex-col items-stretch focus:bg-blue-50">
                  <Link className="flex items-center gap-2" to="/support">
                    <HelpCircle className="size-4" />
                    <p className="text-sm font-medium text-gray-950">Support / Aide</p>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    auth.updateAuth({
                      user: undefined,
                      role: null,
                      isAuthenticated: false,
                    })
                  }}
                  className="gap-2 focus:bg-blue-50"
                >
                  <img className="size-4" src="/icons/logout.svg" alt="logout" />
                  <p className="text-sm font-medium text-gray-950">Se déconnecter</p>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="h-70px shadow-custom flex w-full items-center justify-between rounded-[68px] bg-white px-6 py-2 md:hidden">
        <div className="flex items-center gap-4">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="shrink-0">
                <Menu className="size-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="flex flex-col gap-6">
              <SheetHeader>
                <SheetTitle className="text-left">Menu</SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-2">
                <HeaderNavigationLinks grade={resourceGrade} mobile />
              </div>
              <div className="mt-auto border-t border-gray-400 pt-6">
                <div className="flex flex-col gap-4">
                  <Link
                    to="/profile"
                    className="flex items-center gap-3 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <User className="size-5" />
                    Profil
                  </Link>
                  <Link
                    to="/support"
                    className="flex items-center gap-3 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <HelpCircle className="size-5" />
                    Support / Aide
                  </Link>
                  <button
                    onClick={() => {
                      auth.updateAuth({
                        user: undefined,
                        role: null,
                        isAuthenticated: false,
                      })
                    }}
                    className="flex items-center gap-3 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                  >
                    <img className="size-5" src="/icons/logout.svg" alt="logout" />
                    Se déconnecter
                  </button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
          <Link to="/">
            <img
              src="/logos/viastud-text-logo.png"
              alt="viastud logo"
              className={window.innerWidth < 460 ? 'hidden' : 'h-6 w-auto'}
            />
          </Link>
        </div>

        <div className="flex items-center gap-2">
          {subscriptionLoading ? (
            <div className="h-8 w-8 animate-pulse rounded-full bg-gray-100" />
          ) : (
            <>
              {subscription?.status !== 'ACTIVE' && (
                <Link to="/settings">
                  <Button variant="default" size="sm">
                    S&#39;abonner
                  </Button>
                </Link>
              )}
              {auth.role === 'STUDENT' && tokenBalance && (
                <div className="flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-2">
                  <Clock className="size-3.5 text-gray-600" />
                  <span className="text-xs font-bold text-gray-700">{tokenBalance.balance}</span>
                </div>
              )}
              {subscription?.status === 'ACTIVE' && (
                <div className="flex items-center gap-1.5 rounded-full border border-yellow-300 bg-gradient-to-r from-yellow-300 via-amber-300 to-yellow-400 px-2 py-1 text-[10px] font-bold text-gray-900 shadow-sm">
                  {String(subscription?.planId) === '1' ? (
                    <Star className="size-3 text-yellow-900" />
                  ) : (
                    <Crown className="size-3 text-yellow-900" />
                  )}
                  <span>PREMIUM</span>
                </div>
              )}
              <Link to="/profile">
                <UserIcon
                  firstName={auth.user?.firstName}
                  lastName={auth.user?.lastName}
                  className="size-8"
                />
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
